import { useRef, useState, type FormEvent } from 'react';

import type {
  DiagnosticV1Assessment,
  DiagnosticV1FloorSnapshot,
  DiagnosticV1FeedbackDraft,
  DiagnosticV1FeedbackKind,
} from '../types/active-reasoning-v1';

interface FeedbackFormProps {
  assessment: DiagnosticV1Assessment;
  snapshot: DiagnosticV1FloorSnapshot;
  onSubmit: (feedback: DiagnosticV1FeedbackDraft) => Promise<void>;
  allowCheckFeedback: boolean;
  initialKind?: DiagnosticV1FeedbackKind;
  openForResult?: boolean;
}

const labels: Record<DiagnosticV1FeedbackKind, string> = {
  CORRECT_SOURCE: 'Correct an earlier report',
  CHALLENGE_ASSESSMENT: 'Correct Ace’s understanding',
  CHECK_COMPLETED: 'Record a completed check',
  CHECK_UNAVAILABLE: 'Mark a check unavailable',
  CHECK_SUPERSEDED: 'Mark a check superseded',
  CHECK_DECLINED: 'Decline a suggested check',
  REQUEST_REASSESSMENT: 'Request another assessment',
};

/** C.18 uses a caller-supplied authenticated API handler. This component does
 * not authorize work, infer a cause, or schedule an AI job. It is deliberately
 * not mounted until the backend provides a trusted current assessment/snapshot.
 */
export function ActiveReasoningV1FeedbackForm({ assessment, snapshot, onSubmit, allowCheckFeedback, initialKind = 'CHALLENGE_ASSESSMENT', openForResult = false }: FeedbackFormProps) {
  const [kind, setKind] = useState<DiagnosticV1FeedbackKind>(initialKind);
  const [statement, setStatement] = useState('');
  const [rationale, setRationale] = useState('');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);
  // Keep one ID for a retry of the same payload, reset it when edited.
  const requestId = useRef<string | null>(null);
  const hasCheck = allowCheckFeedback && assessment.outcome === 'NEXT_CHECK_AVAILABLE' && Boolean(assessment.next_check);
  const kinds = (Object.keys(labels) as DiagnosticV1FeedbackKind[]).filter(
    (item) => (!['CHECK_COMPLETED', 'CHECK_UNAVAILABLE', 'CHECK_SUPERSEDED', 'CHECK_DECLINED'].includes(item) || hasCheck)
      && (item !== 'CORRECT_SOURCE' || snapshot.sources.length > 0),
  );

  function edited() {
    requestId.current = null;
    setError('');
    setAccepted(false);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || !statement.trim() || !rationale.trim() || (kind === 'CORRECT_SOURCE' && !target)) return;
    if (!requestId.current) requestId.current = crypto.randomUUID();
    const payload: DiagnosticV1FeedbackDraft = {
      request_id: requestId.current,
      assessment_id: assessment.assessment_id,
      assessment_version: assessment.assessment_version,
      snapshot_id: assessment.snapshot_id,
      kind,
      statement: statement.trim(),
      rationale: rationale.trim(),
      source_reference: kind === 'CORRECT_SOURCE' ? target : null,
      check_id: kind.startsWith('CHECK_') ? assessment.next_check?.identifier ?? null : null,
    };
    setBusy(true);
    setError('');
    try {
      await onSubmit(payload);
      setAccepted(true);
      setStatement('');
      setRationale('');
      setTarget('');
      requestId.current = null;
    } catch {
      setError('Feedback was not confirmed. Refresh the assessment if it changed, then retry.');
    } finally {
      setBusy(false);
    }
  }

  return <details className="r42-v1-disclosure r42-v1-feedback" open={openForResult ? true : undefined}>
    <summary>Correct an assessment or report a check result</summary>
    <div className="r42-v1-feedback-body">
      <p className="r42-v1-assessment-note">Your statement is recorded as attributed evidence, not as a confirmed cause, approved work order, or automatic closure. Record only checks actually performed; identify any simulated result explicitly.</p>
      <form onSubmit={(event) => { void submit(event); }}>
        <label htmlFor="r42-feedback-kind">What would you like to report?</label>
        <select id="r42-feedback-kind" value={kind} disabled={busy} onChange={(event) => {
          edited(); setKind(event.target.value as DiagnosticV1FeedbackKind);
        }}>
          {kinds.map((item) => <option key={item} value={item}>{labels[item]}</option>)}
        </select>
        {kind === 'CORRECT_SOURCE' ? <>
          <label htmlFor="r42-feedback-original">Original source to correct</label>
          <select id="r42-feedback-original" value={target} required disabled={busy} onChange={(event) => {
            edited(); setTarget(event.target.value);
          }}>
            <option value="">Choose a recorded source</option>
            {snapshot.sources.map((source) => <option value={source.reference} key={source.reference}>{source.label}{source.historical ? ' (historical)' : ''}</option>)}
          </select>
        </> : null}
        {kind.startsWith('CHECK_') && assessment.next_check ? <p className="r42-v1-assessment-note"><strong>Check:</strong> {assessment.next_check.text}</p> : null}
        <label htmlFor="r42-feedback-statement">What did you observe or want to correct?</label>
        <textarea id="r42-feedback-statement" required maxLength={2200} rows={3} value={statement} disabled={busy} onChange={(event) => {
          edited(); setStatement(event.target.value);
        }} />
        <label htmlFor="r42-feedback-rationale">Relevant context or reasoning</label>
        <textarea id="r42-feedback-rationale" required maxLength={1000} rows={2} value={rationale} disabled={busy} onChange={(event) => {
          edited(); setRationale(event.target.value);
        }} />
        <button type="submit" className="btn r42-btn-primary" disabled={busy || !statement.trim() || !rationale.trim() || (kind === 'CORRECT_SOURCE' && !target)}>
          {busy ? 'Recording feedback…' : 'Record feedback'}
        </button>
        {error ? <p role="alert" className="r42-v1-feedback-error">{error}</p> : null}
        {accepted ? <p role="status">Feedback was accepted by the connected application. A reassessment may still be pending.</p> : null}
      </form>
    </div>
  </details>;
}
